/**
 * 施術詳細ページのコンテンツデータ。
 * NOTE: 正式な料金・製剤名・使用量・症例数・効果を断定する表現は掲載しない。
 *       未確定の項目は「準備中」として、後から入力できる構造にしている。
 */

export type TreatmentSubItem = {
  id: string;
  name: string;
  /** 個別施術詳細ページのスラッグ（設定時は詳細ページへリンクする） */
  slug?: string;
};

export type TreatmentMenuItem = {
  id: string;
  groupEn?: string;
  group?: string;
  title: string;
  text: string;
  /** 個別施術詳細ページへのパス（設定時はタイトルから遷移できる） */
  href?: string;
  /** 個別施術詳細ページへの明示的なCTAラベル（設定時のみ表示。未設定カテゴリは従来通り） */
  detailLabel?: string;
  /** ページ内アンカー（設定時はタイトルからページ内セクションへ遷移できる） */
  anchor?: string;
  /** 料金セクション内の該当カテゴリへのアンカー（設定時は「料金を見る」がページ内料金へ移動する） */
  priceAnchor?: string;
  /** お悩みに紐づく施術名（今後、各施術詳細へ接続できる構造） */
  treatments?: TreatmentSubItem[];
};

/** カテゴリ（BOTOX / HYALURONIC ACID 等）と、その部位一覧 */
export type TreatmentMenuGroup = {
  id: string;
  en?: string;
  jp: string;
  lead?: string;
  /** 個別施術詳細ページへのパス（設定時はグループから詳細ページへ遷移できる） */
  href?: string;
  items: TreatmentSubItem[];
};

/** 番号付きで示す施術の選択肢（例：2つの注入治療） */
export type TreatmentOptionItem = {
  no: string;
  en: string;
  jp: string;
  text: string;
  href: string;
  ctaLabel: string;
};

export type TreatmentOptions = {
  en: string;
  jp: string;
  lead?: string;
  items: TreatmentOptionItem[];
};

/** 4分類など、施術方式ごとにまとめて示す施術一覧の1分類 */
export type TreatmentCategoryItem = {
  id: string;
  no: string;
  en: string;
  jp: string;
  lead?: string;
  items: { name: string; href: string; note?: string; summary?: string }[];
};

export type TreatmentCategories = {
  en: string;
  jp: string;
  lead?: string;
  categories: TreatmentCategoryItem[];
};

export type TreatmentPageData = {
  slug: string;
  priceTab: string;
  en: string;
  jp: string;
  heroLead: string;
  heroNote: string;
  /** FV画像。後からURLを差し込むだけで差し替え可能（空の間はプレースホルダー表示） */
  heroImage?: string;
  /** スマートフォン幅で切り替えるFV画像（指定時はレスポンシブに切替） */
  heroImageMobile?: string;
  /** FV画像の object-position（md: を含むレスポンシブclass可。人物の見え方を調整） */
  heroObjectPosition?: string;
  /**
   * FV画像枠のクラス。未指定のカテゴリは既定の固定高さ（280/360/440px）。
   * 再生医療カテゴリのみ詳細ページと揃えるため aspect-[3/2] w-full を指定する。
   */
  heroFrameClass?: string;
  heroImageAlt: string;
  menuGroups?: TreatmentMenuGroup[];
  concerns: string[];
  about: { paragraphs: string[]; note?: string };
  features: { no: string; title: string; text: string }[];
  menu: { title: string; lead?: string; items: TreatmentMenuItem[] };
  /** 設定したカテゴリは、治療メニューを番号付きの選択肢レイアウトで表示する */
  treatmentOptions?: TreatmentOptions;
  /** 設定したカテゴリは、施術一覧を施術方式ごと（4分類など）に整理して表示する */
  treatmentCategories?: TreatmentCategories;
  jump?: { title: string; lead?: string; items: { label: string; target: string }[] };
  flow: { no: string; title: string; text: string }[];
  /** 施術情報（METHOD）。分類・施術方法・使用製剤／使用機器の3項目を想定。 */
  method?: { label: string; value: string }[];
  /** 設定したカテゴリは、ページ内目次をこの項目で上書きする（未設定は共通目次）。 */
  indexItems?: { label: string; target: string }[];
  /** true のとき、料金セクションを専用LP導線（SlimZeroLpLink）で表示する。 */
  priceLp?: boolean;
  specs: { label: string; value: string }[];
  risks: string[];
  riskNote: string;
  faqs: { q: string; a: string }[];
  ctaLabel: string;
};

export const treatmentIndexItems = [
  { label: "お悩み", target: "tr-concerns" },
  { label: "施術について", target: "tr-about" },
  { label: "特徴", target: "tr-features" },
  { label: "治療方法・メニュー", target: "tr-menu" },
  { label: "施術の流れ", target: "tr-flow" },
  { label: "料金", target: "tr-price" },
  { label: "施術時間・ダウンタイム", target: "tr-spec" },
  { label: "リスク・注意事項", target: "tr-risk" },
  { label: "Q&A", target: "tr-faq" },
];

const commonFlow = [
  { no: "01", title: "ご予約", text: "WEB予約・LINE・お電話より、ご希望の日時をお知らせください。" },
  { no: "02", title: "カウンセリング", text: "お悩みやご希望、生活スタイルを丁寧にお伺いします。" },
  { no: "03", title: "医師の診察", text: "状態を確認し、適した方法をご提案します。" },
  { no: "04", title: "施術", text: "内容・料金・リスクについてご説明し、ご納得のうえで施術を行います。" },
  { no: "05", title: "アフターケア", text: "施術後の経過を確認し、ホームケアをご案内します。" },
];

const commonSpecs = (extraLabel: string, extraValue: string) => [
  { label: "施術時間目安", value: "内容・範囲により異なります" },
  { label: "ダウンタイム目安", value: "個人差があります" },
  { label: "来院ペース", value: "状態に合わせてご提案します" },
  { label: extraLabel, value: extraValue },
];

export const treatmentPages: TreatmentPageData[] = [
  {
    slug: "hair-removal",
    priceTab: "hair-removal",
    en: "MEDICAL HAIR REMOVAL",
    jp: "医療脱毛",
    heroLead: "一人ひとりの毛質や肌状態に合わせて、\n医療レーザー脱毛をご提案します。",
    heroNote: "施術画像は準備中です",
    heroImage:
      "/images/site/sbdc-hair-removal-fv-v1.webp",
    heroObjectPosition: "object-center",
    heroImageAlt: "渋谷美容皮膚科クリニックの医療脱毛",
    concerns: [
      "自己処理の負担を減らしたい",
      "肌トラブルを繰り返したくない",
      "ムダ毛を気にせず過ごしたい",
      "部位ごとにきれいに整えたい",
      "メンズとして全身を整えたい",
      "処理にかかる時間を減らしたい",
    ],
    about: {
      paragraphs: [
        "医療脱毛は、医療機関で行うレーザー脱毛です。",
        "SBDCでは、毛質や肌状態、施術部位を確認し、医師の診察のもと施術をご案内します。",
        "照射条件や施術内容は、肌状態や部位に応じて判断します。",
      ],
    },
    features: [
      { no: "01", title: "毛質・肌状態を確認", text: "毛質や肌状態を確認し、施術内容をご案内します。" },
      { no: "02", title: "女性・男性ともに対応", text: "レディース・メンズともに医療脱毛をご相談いただけます。" },
      { no: "03", title: "複数の部位に対応", text: "全身、顔、VIO、ヒゲ、部位別メニューをご案内します。" },
      { no: "04", title: "医師の診察のもと施術", text: "肌状態を確認し、医師の診察のもと施術の適応を判断します。" },
    ],
    menu: {
      title: "対応メニュー",
      lead: "目的や部位に合わせてお選びいただけます。詳しい内容はカウンセリングでご案内します。",
      items: [
        { id: "menu-hair-ladies", priceAnchor: "hair-ladies", groupEn: "LADIES", group: "レディース", title: "レディース", text: "女性向けの医療脱毛。" },
        { id: "menu-hair-mens", priceAnchor: "hair-mens", groupEn: "MENS", group: "メンズ", title: "メンズ", text: "男性向けの医療脱毛。" },
        { id: "menu-hair-full", priceAnchor: "hair-ladies-full", groupEn: "FULL BODY", group: "全身", title: "全身", text: "全身の医療脱毛。" },
        { id: "menu-hair-face", priceAnchor: "hair-ladies-face", groupEn: "FACE", group: "顔", title: "顔", text: "顔まわりの医療脱毛。" },
        { id: "menu-hair-vio", priceAnchor: "hair-ladies-vio", groupEn: "VIO", group: "VIO", title: "VIO", text: "VIOの医療脱毛。" },
        { id: "menu-hair-beard", priceAnchor: "hair-mens-beard", groupEn: "BEARD", group: "ヒゲ", title: "ヒゲ", text: "ヒゲの医療脱毛。" },
        { id: "menu-hair-parts", priceAnchor: "hair-mens-body", groupEn: "PARTS", group: "部位別", title: "部位別", text: "気になる部位を選ぶ医療脱毛。" },
      ],
    },
    jump: {
      title: "部位から探す",
      lead: "気になる部位を選ぶと、該当するメニューへ移動します。",
      items: [
        { label: "レディース", target: "menu-hair-ladies" },
        { label: "メンズ", target: "menu-hair-mens" },
        { label: "全身", target: "menu-hair-full" },
        { label: "顔", target: "menu-hair-face" },
        { label: "VIO", target: "menu-hair-vio" },
        { label: "ヒゲ", target: "menu-hair-beard" },
        { label: "部位別", target: "menu-hair-parts" },
      ],
    },
    flow: [
      { no: "01", title: "ご予約", text: "WEB・LINE・電話からご予約ください。" },
      { no: "02", title: "カウンセリング", text: "脱毛したい部位やご希望、肌状態などを確認します。" },
      { no: "03", title: "医師の診察", text: "肌状態を確認し、施術の適応を判断します。" },
      { no: "04", title: "治療内容・料金の説明", text: "施術内容、料金、リスク・注意事項をご説明します。" },
      { no: "05", title: "施術", text: "内容に同意いただいたうえで施術を行います。" },
      { no: "06", title: "アフターケア", text: "施術後の注意事項をご案内します。" },
    ],
    method: [
      { label: "分類", value: "MEDICAL HAIR REMOVAL" },
      { label: "施術方法", value: "医療レーザー脱毛" },
      { label: "使用機器", value: "確認中" },
    ],
    specs: [
      { label: "施術時間", value: "—" },
      { label: "麻酔", value: "—" },
      { label: "ダウンタイム", value: "—" },
      { label: "推奨回数", value: "—" },
      { label: "推奨間隔", value: "—" },
    ],
    risks: [
      "施術部位の赤み・腫れ・ほてりが出る場合があります。",
      "毛嚢炎などの肌トラブルが生じることがあります。",
      "色素沈着・色素脱失が生じる可能性があります。",
      "日焼けや肌状態によっては、施術をお受けいただけない場合があります。",
      "症状や経過には個人差があります。",
    ],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
    faqs: [
      { q: "医療脱毛は痛みがありますか？", a: "痛みの感じ方には個人差があります。施術部位や肌状態を確認しながらご案内します。" },
      { q: "何回くらい通う必要がありますか？", a: "毛質や部位、肌状態などによって異なります。正式な推奨回数は確認中です。" },
      { q: "男性でも受けられますか？", a: "はい。SBDCではメンズの医療脱毛もご案内しています。" },
      { q: "日焼けしていても受けられますか？", a: "肌状態によっては施術をお受けいただけない場合があります。診察時に確認します。" },
      { q: "どの部位を脱毛できますか？", a: "レディース・メンズ、全身、顔、VIO、ヒゲ、部位別のメニューをご案内しています。詳しい範囲はカウンセリング時にご説明します。" },
    ],
    ctaLabel: "この施術についてLINEで相談",
  },
  {
    slug: "slim-zero",
    priceTab: "slim-zero",
    en: "MEDICAL SLIM ZERO",
    jp: "医療痩身・ダイエット",
    heroLead: "体型・部位・目的に合わせて、\n治療方法を検討する医療痩身プログラム。",
    heroNote: "施術画像は準備中です",
    heroImage:
      "/images/site/wide_bright_airy_lifestyle_beauty_fitness_photog.webp",
    heroImageMobile:
      "/images/site/medical_slim_zero_mobile_fv.webp",
    heroObjectPosition: "object-[58%_center] md:object-[76%_center]",
    heroImageAlt: "医療痩身の施術イメージ",
    concerns: [
      "部分痩せしたい",
      "落ちにくい脂肪が気になる",
      "体型を整えたい",
      "運動や食事制限だけでは難しい",
      "部位ごとにアプローチしたい",
      "ライフスタイルに合わせて続けたい",
    ],
    about: {
      paragraphs: [
        "Medical Slim ZEROは、ひとつの施術を勧めるのではなく、体型・部位・目的に合わせて治療方法を検討する医療痩身プログラムです。",
        "脂肪の状態や生活習慣は一人ひとり異なります。カウンセリングと医師の診察をもとに、複数の治療を組み合わせてご提案します。",
        "医療広告ガイドラインに配慮し、効果を断定する表現は行いません。",
      ],
      note: "「どれか1つを売る」ためのものではなく、目的に合わせて治療方法を検討します。",
    },
    features: [
      { no: "01", title: "組み合わせで考える", text: "目的に応じて、複数の治療方法を組み合わせて検討します。" },
      { no: "02", title: "部位ごとのアプローチ", text: "気になる部位や範囲に合わせてご提案します。" },
      { no: "03", title: "ライフスタイルに合わせて", text: "通いやすさや生活習慣を考慮して計画します。" },
      { no: "04", title: "カウンセリング重視", text: "現在の状態を確認したうえで、無理のない計画を提案します。" },
    ],
    menu: {
      title: "主要な治療",
      lead: "以下は当院で検討する主要な治療方法です。内容は医師の診察をもとにご提案します。",
      items: [
        { id: "slim-injection", href: "/slim/injection", groupEn: "INJECTION", group: "新脂肪溶解注射", title: "新脂肪溶解注射", text: "注射により脂肪にアプローチする治療です。使用する製剤や適応はカウンセリングでご案内します。" },
        { id: "slim-rf", href: "/slim/rf", groupEn: "RF", group: "RF（ラジオ波）", title: "RF（ラジオ波）", text: "ラジオ波による温熱を用いた治療です。照射範囲や回数はご相談ください。" },
        { id: "slim-cool", href: "/slim/cooling", groupEn: "COOL", group: "脂肪冷却", title: "脂肪冷却", text: "冷却により脂肪細胞にアプローチする治療です。適応は診察にて確認します。" },
        { id: "slim-ems", href: "/slim/ems", groupEn: "EMS", group: "医療用EMS", title: "医療用EMS", text: "筋肉にアプローチする医療用EMSを用いた治療です。" },
      ],
    },
    jump: {
      title: "治療方法から探す",
      lead: "気になる治療方法を選ぶと、該当する説明へ移動します。",
      items: [
        { label: "新脂肪溶解注射", target: "slim-injection" },
        { label: "RF（ラジオ波）", target: "slim-rf" },
        { label: "脂肪冷却", target: "slim-cool" },
        { label: "医療用EMS", target: "slim-ems" },
      ],
    },
    flow: commonFlow,
    method: [
      { label: "分類", value: "MEDICAL SLIM ZERO" },
      { label: "施術方法", value: "医療痩身" },
      { label: "使用製剤・機器", value: "確認中" },
    ],
    specs: [
      { label: "施術時間", value: "—" },
      { label: "麻酔", value: "—" },
      { label: "ダウンタイム", value: "—" },
      { label: "推奨回数", value: "—" },
      { label: "推奨間隔", value: "—" },
    ],
    risks: [
      "治療方法によって、赤み、腫れ、痛み、内出血、違和感などが生じる場合があります。",
      "治療ごとにリスク・副作用・注意事項が異なります。",
      "身体の状態や施術部位によっては、治療をお受けいただけない場合があります。",
      "詳しい内容は各施術ページおよび診察時にご説明します。",
      "症状や経過には個人差があります。",
    ],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
    priceLp: true,
    indexItems: [
      { label: "お悩み", target: "tr-concerns" },
      { label: "施術について", target: "tr-about" },
      { label: "特徴", target: "tr-features" },
      { label: "施術情報", target: "tr-method" },
      { label: "治療方法・メニュー", target: "tr-menu" },
      { label: "施術の流れ", target: "tr-flow" },
      { label: "料金", target: "tr-price" },
      { label: "施術時間・ダウンタイム", target: "tr-spec" },
      { label: "リスク・注意事項", target: "tr-risk" },
      { label: "Q&A", target: "tr-faq" },
    ],
    faqs: [
      { q: "どの治療を選べばよいか分かりません。", a: "カウンセリングと診察のうえで、目的や部位に合わせてご提案します。" },
      { q: "複数の治療を組み合わせられますか？", a: "はい。状態に合わせて組み合わせを検討します。" },
      { q: "痛みはありますか？", a: "感じ方には個人差があります。内容に応じてご説明します。" },
      { q: "効果はどのくらいで実感できますか？", a: "効果・経過には個人差があります。断定した表現は行いません。" },
      { q: "通う頻度を教えてください。", a: "治療内容や状態によって異なるため、カウンセリングでご案内します。" },
    ],
    ctaLabel: "この施術についてLINEで相談",
  },
  {
    slug: "skin-management",
    priceTab: "skin",
    en: "SKIN MANAGEMENT",
    jp: "お肌管理",
    heroLead: "お悩みから探せる、\nお肌管理のご案内。",
    heroNote: "施術画像は準備中です",
    heroImage:
      "/images/site/sbdc-skin-management-fv-v3.webp",
    heroObjectPosition: "object-center",
    heroFrameClass: "aspect-[3/2] w-full",
    heroImageAlt: "お肌管理の施術イメージ",
    concerns: ["ニキビ", "シミ・くすみ", "毛穴", "たるみ・シワ", "肌質改善", "ハリ・ツヤ"],
    about: {
      paragraphs: [
        "お肌管理では、施術名からではなく、気になるお悩みから美容医療を探していただけるようにしています。",
        "肌状態は一人ひとり異なります。今の肌に必要なケアと、これからの肌のためのケアを組み合わせてご提案します。",
        "お悩みに応じた施術を一覧でご案内しています。詳しい内容はカウンセリングでご説明します。",
      ],
    },
    features: [
      { no: "01", title: "悩みから探せる", text: "施術名が分からなくても、気になるお悩みから選べます。" },
      { no: "02", title: "肌状態を確認", text: "カウンセリングと診察で肌状態を確認します。" },
      { no: "03", title: "今と未来のケア", text: "現在の悩みと、これからの肌を考えて組み立てます。" },
      { no: "04", title: "無理のないご提案", text: "ご希望と状態に合わせて、無理のない内容を提案します。" },
    ],
    treatmentCategories: {
      en: "TREATMENTS",
      jp: "施術一覧",
      lead: "お肌管理では、4つの施術方法ごとにご案内しています。気になる施術名を選ぶと、詳細をご確認いただけます。",
      categories: [
        {
          id: "skin-plasma",
          no: "01",
          en: "PLASMA",
          jp: "プラズマ",
          lead: "プラズマをベースに、使用する製剤を組み合わせてご案内する施術です。",
          items: [
            { name: "ヴィーナス ステムサップ", href: "/skin/venus-stemsup", note: "StemSup", summary: "プラズマとStemSupを組み合わせたお肌管理。" },
            { name: "ヴィーナス ダイヤモンド", href: "/skin/venus-diamond", note: "リジュラン", summary: "プラズマとリジュランを組み合わせたお肌管理。" },
            { name: "プラズマ", href: "/skin/plasma", summary: "プラズマを利用した美容目的のお肌管理。" },
          ],
        },
        {
          id: "skin-dermapen4",
          no: "02",
          en: "DERMAPEN 4",
          jp: "ダーマペン4",
          lead: "ダーマペン4をベースに、目的に合わせて組み合わせてご案内する施術です。",
          items: [
            { name: "シンデレラ ステムサップ", href: "/skin/cinderella-stemsup", note: "StemSup", summary: "ダーマペン4とStemSupを組み合わせたお肌管理。" },
            { name: "シンデレラ ダイヤモンド", href: "/skin/cinderella-diamond", note: "リジュラン", summary: "ダーマペン4とリジュランを組み合わせたお肌管理。" },
            { name: "ヴェルヴェットスキン", href: "/skin/velvet-skin", note: "ダーマペン4＋コラーゲンピール", summary: "ダーマペン4とコラーゲンピールを組み合わせた施術。" },
          ],
        },
        {
          id: "skin-peeling",
          no: "03",
          en: "PEELING",
          jp: "ピーリング",
          items: [
            { name: "ラクトピール", href: "/skin/lacto-peel", summary: "乳酸を使用した、比較的マイルドなケミカルピーリング。" },
            { name: "コラーゲンピール", href: "/skin/collagen-peel", summary: "薬剤を塗布して行う、肌管理のためのピーリング施術。" },
            { name: "MIXピール", href: "/skin/mix-peel", summary: "複数のピーリング成分を組み合わせたケミカルピーリング。" },
          ],
        },
        {
          id: "skin-photo-facial",
          no: "04",
          en: "PHOTO FACIAL",
          jp: "光治療",
          items: [
            { name: "ホワイトフォトフェイシャル", href: "/skin/white-photo-facial", summary: "光エネルギーを利用した、顔全体のお肌管理。" },
          ],
        },
      ],
    },
    menu: {
      title: "お悩み別のご案内",
      lead: "気になるお悩みをお選びください。詳細はカウンセリングでご案内します。",
      items: [
        {
          id: "skin-acne",
          groupEn: "ACNE",
          group: "ニキビ",
          title: "ニキビ",
          text: "ニキビのお悩みに応じた施術をご案内します。",
          treatments: [
            { id: "skin-acne-venus-stemsup", name: "ヴィーナス ステムサップ", slug: "venus-stemsup" },
            { id: "skin-acne-venus-diamond", name: "ヴィーナス ダイヤモンド", slug: "venus-diamond" },
            { id: "skin-acne-cinderella-stemsup", name: "シンデレラ ステムサップ", slug: "cinderella-stemsup" },
            { id: "skin-acne-cinderella-diamond", name: "シンデレラ ダイヤモンド", slug: "cinderella-diamond" },
            { id: "skin-acne-white-photo-facial", name: "ホワイトフォトフェイシャル", slug: "white-photo-facial" },
            { id: "skin-acne-plasma", name: "プラズマ", slug: "plasma" },
            { id: "skin-acne-mix-peel", name: "MIXピール", slug: "mix-peel" },
          ],
        },
        {
          id: "skin-pigment",
          groupEn: "PIGMENT",
          group: "シミ・くすみ",
          title: "シミ・くすみ",
          text: "シミ・くすみのお悩みに応じた施術をご案内します。",
          treatments: [
            { id: "skin-pigment-venus-stemsup", name: "ヴィーナス ステムサップ", slug: "venus-stemsup" },
            { id: "skin-pigment-venus-diamond", name: "ヴィーナス ダイヤモンド", slug: "venus-diamond" },
            { id: "skin-pigment-cinderella-stemsup", name: "シンデレラ ステムサップ", slug: "cinderella-stemsup" },
            { id: "skin-pigment-cinderella-diamond", name: "シンデレラ ダイヤモンド", slug: "cinderella-diamond" },
            { id: "skin-pigment-white-photo-facial", name: "ホワイトフォトフェイシャル", slug: "white-photo-facial" },
            { id: "skin-pigment-plasma", name: "プラズマ", slug: "plasma" },
            { id: "skin-pigment-lacto-peel", name: "ラクトピール", slug: "lacto-peel" },
          ],
        },
        {
          id: "skin-pores",
          groupEn: "PORES",
          group: "毛穴",
          title: "毛穴",
          text: "毛穴のお悩みに応じた施術をご案内します。",
          treatments: [
            { id: "skin-pores-venus-stemsup", name: "ヴィーナス ステムサップ", slug: "venus-stemsup" },
            { id: "skin-pores-venus-diamond", name: "ヴィーナス ダイヤモンド", slug: "venus-diamond" },
            { id: "skin-pores-cinderella-stemsup", name: "シンデレラ ステムサップ", slug: "cinderella-stemsup" },
            { id: "skin-pores-cinderella-diamond", name: "シンデレラ ダイヤモンド", slug: "cinderella-diamond" },
            { id: "skin-pores-white-photo-facial", name: "ホワイトフォトフェイシャル", slug: "white-photo-facial" },
          ],
        },
        {
          id: "skin-lifting",
          groupEn: "LIFTING",
          group: "たるみ・シワ",
          title: "たるみ・シワ",
          text: "たるみ・シワのお悩みに応じた施術をご案内します。",
          treatments: [
            { id: "skin-lifting-venus-stemsup", name: "ヴィーナス ステムサップ", slug: "venus-stemsup" },
            { id: "skin-lifting-venus-diamond", name: "ヴィーナス ダイヤモンド", slug: "venus-diamond" },
            { id: "skin-lifting-cinderella-stemsup", name: "シンデレラ ステムサップ", slug: "cinderella-stemsup" },
            { id: "skin-lifting-cinderella-diamond", name: "シンデレラ ダイヤモンド", slug: "cinderella-diamond" },
            { id: "skin-lifting-plasma", name: "プラズマ", slug: "plasma" },
            { id: "skin-lifting-collagen-peel", name: "コラーゲンピール", slug: "collagen-peel" },
            { id: "skin-lifting-white-photo-facial", name: "ホワイトフォトフェイシャル", slug: "white-photo-facial" },
          ],
        },
        {
          id: "skin-texture",
          groupEn: "TEXTURE",
          group: "肌質改善",
          title: "肌質改善",
          text: "対応する施術は、肌状態に合わせてカウンセリングでご案内します。",
        },
        {
          id: "skin-glow",
          groupEn: "GLOW",
          group: "ハリ・ツヤ",
          title: "ハリ・ツヤ",
          text: "対応する施術は、肌状態に合わせてカウンセリングでご案内します。",
        },
      ],
    },
    jump: {
      title: "お悩みから探す",
      lead: "気になるお悩みを選ぶと、該当する項目へ移動します。",
      items: [
        { label: "ニキビ", target: "skin-acne" },
        { label: "シミ・くすみ", target: "skin-pigment" },
        { label: "毛穴", target: "skin-pores" },
        { label: "たるみ・シワ", target: "skin-lifting" },
        { label: "肌質改善", target: "skin-texture" },
        { label: "ハリ・ツヤ", target: "skin-glow" },
      ],
    },
    flow: commonFlow,
    specs: commonSpecs("麻酔", "内容に応じてご相談ください"),
    risks: [
      "赤み・腫れ・ほてりが出る場合があります。",
      "施術部位に一時的な反応が生じることがあります。",
      "肌状態によっては施術をお受けいただけない場合があります。",
      "効果・経過には個人差があります。",
    ],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
    faqs: [
      { q: "どの施術が自分に合うか分かりません。", a: "お悩みを伺い、肌状態を確認したうえでご提案します。" },
      { q: "カウンセリングだけでも相談できますか？", a: "はい。施術を決めていない段階でのご相談も歓迎しています。" },
      { q: "施術内容はどこで確認できますか？", a: "正式な内容が決まり次第、本ページおよびカウンセリングにてご案内します。" },
      { q: "肌が敏感でも相談できますか？", a: "肌状態を確認のうえ、ご相談ください。" },
    ],
    ctaLabel: "お肌の悩みをLINEで相談",
  },
  {
    slug: "regenerative",
    priceTab: "regenerative",
    en: "REGENERATIVE MEDICINE",
    jp: "再生医療",
    heroLead: "StemSup点滴\n幹細胞培養上清液を用いた点滴メニュー。",
    heroNote: "施術画像は準備中です",
    heroImage:
      "/images/site/regenerative-parent-fv.webp",
    heroObjectPosition: "object-[58%_center] md:object-[64%_center]",
    heroFrameClass: "aspect-[3/2] w-full",
    heroImageAlt: "再生医療の施術イメージ",
    concerns: [
      "幹細胞培養上清液について知りたい",
      "点滴メニューを検討したい",
      "医師の診察のもとで相談したい",
      "体調や目的に合わせて検討したい",
      "再生医療について相談したい",
    ],
    about: {
      paragraphs: [
        "再生医療カテゴリでは、幹細胞を培養する過程で得られる幹細胞培養上清液（StemSup）を、点滴で投与するメニューをご案内しています。",
        "本治療で使用するのは、幹細胞そのものではなく、幹細胞を培養する過程で得られる培養上清液を用いた製剤です。誤解を招かないよう、カウンセリングで内容を丁寧にご説明します。",
      ],
      note: "幹細胞そのものを投与する治療ではありません。",
    },
    features: [
      { no: "01", title: "幹細胞培養上清液 StemSupを用いた点滴メニュー", text: "幹細胞を培養する過程で得られる培養上清液を用いた製剤を、点滴で投与するメニューです。" },
      { no: "02", title: "医師の診察のもと治療をご案内", text: "医師の診察のもと、治療の適応を確認したうえでご案内します。" },
      { no: "03", title: "体調や目的を確認したうえで治療内容を検討", text: "体調や目的を確認し、治療内容を検討します。" },
      { no: "04", title: "幹細胞そのものを投与する治療ではありません", text: "本治療で使用するのは、幹細胞そのものではなく、幹細胞培養上清液を用いた製剤です。" },
    ],
    menu: {
      title: "治療内容",
      lead: "以下の内容についてご案内します。詳細はカウンセリングでご説明します。",
      items: [
        { id: "reg-stemsup-drip", href: "/regenerative/stemsup-drip", groupEn: "STEMSUP IV THERAPY", group: "StemSup点滴", title: "StemSup点滴", text: "幹細胞を培養する過程で得られる幹細胞培養上清液 StemSupを、点滴で投与する、再生医療カテゴリのご案内メニューです。", detailLabel: "StemSup点滴について詳しく見る" },
        { id: "reg-stemsup", anchor: "culture-supernatant", groupEn: "STEMSUP", group: "幹細胞培養上清液 StemSup", title: "幹細胞培養上清液 StemSup", text: "幹細胞を培養する過程で得られる培養上清液を用いた製剤です。使用する製剤や適応についてはカウンセリングでご案内します。" },
      ],
    },
    jump: {
      title: "内容から探す",
      lead: "詳しく知りたい内容を選ぶと、該当する説明へ移動します。",
      items: [
        { label: "StemSup点滴", target: "reg-stemsup-drip" },
        { label: "幹細胞培養上清液 StemSup", target: "reg-stemsup" },
      ],
    },
    flow: commonFlow,
    specs: commonSpecs("説明・同意", "施術前に十分な説明を行います"),
    risks: [
      "点滴に伴い、穿刺部の痛み、赤み、内出血などが生じる場合があります。",
      "使用製剤に関するリスク・副作用・禁忌については、正式情報を確認のうえ掲載します。",
      "本治療は、幹細胞そのものを投与する治療ではありません。",
    ],
    riskNote: "再生医療・未承認治療等に関する必要な注意事項は、正式な情報が確定し次第、追記します。",
    faqs: [
      { q: "StemSupは幹細胞そのものですか？", a: "いいえ。SBDCで使用するStemSupは、幹細胞そのものではなく、幹細胞を培養する過程で得られる培養上清液を用いた製剤です。" },
      { q: "どのような治療ですか？", a: "幹細胞培養上清液を点滴で投与するメニューです。適応や内容については、診察のうえでご案内します。" },
      { q: "施術時間はどのくらいですか？", a: "正式な施術時間は確認中です。確定次第掲載します。" },
      { q: "料金はどこで確認できますか？", a: "料金は料金表ページでご確認いただけます。" },
      { q: "どんな方が対象ですか？", a: "体調や目的を確認のうえ、医師の診察のもとで適応をご案内します。" },
    ],
    ctaLabel: "再生医療についてLINEで相談",
  },
  {
    slug: "injectables",
    priceTab: "injectables",
    en: "INJECTABLES",
    jp: "注入治療",
    heroLead: "顔立ちやお悩みに合わせて、\n細部まで丁寧に。",
    heroNote: "施術画像は準備中です",
    heroImage:
      "/images/site/injectables-parent-fv.webp",
    heroObjectPosition: "object-[62%_center]",
    heroFrameClass: "aspect-[3/2] w-full",
    heroImageAlt: "注入治療の施術イメージ",
    concerns: ["額のシワ", "眉間のシワ", "目尻のシワ", "エラ", "ワキ", "肩", "涙袋", "鼻", "唇", "顎", "ほうれい線", "こめかみ"],
    about: {
      paragraphs: [
        "SBDCでは、表情や顔立ち、気になる部位を確認しながら、一人ひとりに合わせた注入治療をご提案します。",
        "ボトックス、ヒアルロン酸を中心に、医師の診察のもと治療内容をご案内します。",
      ],
    },
    treatmentOptions: {
      en: "TREATMENT OPTIONS",
      jp: "2つの注入治療",
      lead: "注入治療では、ボトックスとヒアルロン酸の2つを中心にご案内しています。詳しい内容はカウンセリングでご説明します。",
      items: [
        {
          no: "01",
          en: "BOTOX",
          jp: "ボトックス",
          text: "表情や筋肉の動きに関するお悩みについてご相談いただける注入治療です。",
          href: "/injectables/botox",
          ctaLabel: "ボトックスについて詳しく見る",
        },
        {
          no: "02",
          en: "HYALURONIC ACID",
          jp: "ヒアルロン酸",
          text: "顔のボリュームや輪郭、バランスについてご相談いただける注入治療です。",
          href: "/injectables/hyaluronic-acid",
          ctaLabel: "ヒアルロン酸について詳しく見る",
        },
      ],
    },
    features: [
      { no: "01", title: "2つのカテゴリ", text: "ボトックスとヒアルロン酸を大きく分けてご案内します。" },
      { no: "02", title: "部位から探せる", text: "気になる部位から該当する治療を探せます。" },
      { no: "03", title: "医師の診察のもとで", text: "状態を確認し、適した方法をご提案します。" },
      { no: "04", title: "丁寧な説明", text: "内容・リスクについて十分にご説明します。" },
    ],
    menu: {
      title: "治療カテゴリ",
      lead: "ボトックスとヒアルロン酸の2つに分けてご案内します。気になる部位をお選びください。",
      items: [],
    },
    menuGroups: [
      {
        id: "inj-botox",
        en: "BOTOX",
        jp: "ボトックス",
        href: "/injectables/botox",
        lead: "対応部位からご案内します。",
        items: [
          { id: "inj-botox-forehead", name: "額" },
          { id: "inj-botox-glabella", name: "眉間" },
          { id: "inj-botox-bunny", name: "バニー" },
          { id: "inj-botox-crow", name: "目尻" },
          { id: "inj-botox-droop", name: "たれ目" },
          { id: "inj-botox-philtrum", name: "人中" },
          { id: "inj-botox-mouth", name: "口角" },
          { id: "inj-botox-gummy", name: "ガミー" },
          { id: "inj-botox-chin", name: "アゴ" },
          { id: "inj-botox-masseter", name: "エラ" },
          { id: "inj-botox-underarm", name: "ワキ" },
          { id: "inj-botox-shoulder", name: "肩" },
          { id: "inj-botox-calf", name: "ふくらはぎ" },
        ],
      },
      {
        id: "inj-ha",
        en: "HYALURONIC ACID",
        jp: "ヒアルロン酸",
        href: "/injectables/hyaluronic-acid",
        lead: "対応部位からご案内します。",
        items: [
          { id: "inj-ha-tear", name: "涙袋" },
          { id: "inj-ha-nose", name: "鼻" },
          { id: "inj-ha-lip", name: "唇" },
          { id: "inj-ha-chin", name: "顎" },
          { id: "inj-ha-neck", name: "首のしわ" },
          { id: "inj-ha-nasolabial", name: "ほうれい線" },
          { id: "inj-ha-mouth", name: "口角" },
          { id: "inj-ha-fine", name: "小じわ" },
          { id: "inj-ha-forehead", name: "額" },
          { id: "inj-ha-temple", name: "こめかみ" },
        ],
      },
    ],
    jump: {
      title: "部位から探す",
      lead: "気になる部位を選ぶと、該当する項目へ移動します。",
      items: [
        { label: "ボトックス 額", target: "inj-botox-forehead" },
        { label: "ボトックス 眉間", target: "inj-botox-glabella" },
        { label: "ボトックス バニー", target: "inj-botox-bunny" },
        { label: "ボトックス 目尻", target: "inj-botox-crow" },
        { label: "ボトックス たれ目", target: "inj-botox-droop" },
        { label: "ボトックス 人中", target: "inj-botox-philtrum" },
        { label: "ボトックス 口角", target: "inj-botox-mouth" },
        { label: "ボトックス ガミー", target: "inj-botox-gummy" },
        { label: "ボトックス アゴ", target: "inj-botox-chin" },
        { label: "ボトックス エラ", target: "inj-botox-masseter" },
        { label: "ボトックス ワキ", target: "inj-botox-underarm" },
        { label: "ボトックス 肩", target: "inj-botox-shoulder" },
        { label: "ボトックス ふくらはぎ", target: "inj-botox-calf" },
        { label: "ヒアルロン酸 涙袋", target: "inj-ha-tear" },
        { label: "ヒアルロン酸 鼻", target: "inj-ha-nose" },
        { label: "ヒアルロン酸 唇", target: "inj-ha-lip" },
        { label: "ヒアルロン酸 顎", target: "inj-ha-chin" },
        { label: "ヒアルロン酸 首のしわ", target: "inj-ha-neck" },
        { label: "ヒアルロン酸 ほうれい線", target: "inj-ha-nasolabial" },
        { label: "ヒアルロン酸 口角", target: "inj-ha-mouth" },
        { label: "ヒアルロン酸 小じわ", target: "inj-ha-fine" },
        { label: "ヒアルロン酸 額", target: "inj-ha-forehead" },
        { label: "ヒアルロン酸 こめかみ", target: "inj-ha-temple" },
      ],
    },
    flow: commonFlow,
    specs: commonSpecs("麻酔", "内容に応じてご相談ください"),
    risks: [
      "赤み・腫れ・内出血・痛みが出る場合があります。",
      "アレルギー反応が生じる可能性があります。",
      "表情や見た目の変化が生じる場合があります。",
      "妊娠中・授乳中など、お受けいただけない場合があります。",
      "効果・経過には個人差があります。",
    ],
    riskNote: "製剤名・使用量・料金などの詳細は、正式な情報が確定し次第、掲載します。",
    faqs: [
      { q: "ボトックスとヒアルロン酸の違いは何ですか？", a: "目的や部位によって適した方法が異なります。診察のうえでご提案します。" },
      { q: "どの部位に対応していますか？", a: "本ページに掲載の部位を中心にご案内しています。" },
      { q: "痛みはありますか？", a: "感じ方には個人差があります。内容に応じてご説明します。" },
      { q: "料金はどこで確認できますか？", a: "料金は料金表ページでご確認いただけます。" },
    ],
    ctaLabel: "注入治療についてLINEで相談",
  },
  {
    slug: "eye",
    priceTab: "eye",
    en: "EYE TREATMENT",
    jp: "二重・目元",
    heroLead: "二重埋没法を中心に、\n目元のお悩みについてご相談いただけます。",
    heroNote: "施術画像は準備中です",
    heroImage:
      "/images/site/sbdc-eye-treatment-fv-v1.webp",
    heroObjectPosition: "object-center",
    heroImageAlt: "渋谷美容皮膚科クリニックの二重・目元治療",
    concerns: [
      "二重にしたい",
      "目元を整えたい",
      "埋没法が気になる",
      "自然な仕上がりを希望したい",
      "ダウンタイムが気になる",
    ],
    about: {
      paragraphs: [
        "二重埋没法を中心に、目元のお悩みについてご相談いただけます。",
        "まぶたの状態やご希望を確認し、医師の診察のもと治療内容をご提案します。",
      ],
    },
    features: [
      { no: "01", title: "二重埋没法を中心に", text: "目元のお悩みに合わせてご提案します。" },
      { no: "02", title: "状態を確認", text: "まぶたの状態を確認し、適した方法を検討します。" },
      { no: "03", title: "医師の診察のもとで", text: "施術前に医師が状態を確認します。" },
      { no: "04", title: "丁寧な説明", text: "内容・リスクについて十分にご説明します。" },
    ],
    menu: {
      title: "治療内容",
      lead: "現在は二重埋没法を中心にご案内しています。",
      items: [
        { id: "eye-double", href: "/eye/double-eyelid", groupEn: "DOUBLE EYELID", group: "二重埋没法", title: "二重埋没法", text: "糸を用いて二重をつくる方法です。適応や術式は診察のうえでご提案します。" },
      ],
    },
    jump: {
      title: "内容から探す",
      lead: "詳しく知りたい内容を選ぶと、該当する説明へ移動します。",
      items: [{ label: "二重埋没法", target: "eye-double" }],
    },
    flow: commonFlow,
    specs: commonSpecs("麻酔", "必要に応じてご相談ください"),
    risks: [
      "腫れ・内出血・痛みが出る場合があります。",
      "二重の幅が変化することがあります。",
      "感染などの可能性があります。",
      "効果・経過には個人差があります。",
    ],
    riskNote: "術式・保証内容・料金などの詳細は、正式な情報が確定し次第、掲載します。",
    faqs: [
      { q: "二重埋没法はどんな方法ですか？", a: "糸を用いて二重をつくる方法です。詳細はカウンセリングでご説明します。" },
      { q: "ダウンタイムはありますか？", a: "個人差があります。内容に応じてご説明します。" },
      { q: "自然な仕上がりになりますか？", a: "仕上がりには個人差があります。断定した表現は行いません。" },
      { q: "料金はどこで確認できますか？", a: "料金は料金表ページでご確認いただけます。" },
    ],
    ctaLabel: "目元のご相談をLINEで",
  },
];

export function getTreatmentPage(slug: string) {
  return treatmentPages.find((page) => page.slug === slug);
}